import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import MainGrid from '../../components/MainGrid';
import {
	AutoAwesome,
	AutoAwesomeOutlined,
	Check,
	Close,
	Edit,
	Shield,
} from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	getAccessToken,
	getElderHomeId,
	getUserId,
	getUserName,
} from '../../Utils/AuthUtil';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
	addOptimizedCarePlan,
	fetchCarePlanById,
	fetchExternalElderPlan,
	fetchExternalLatestElderPlanData,
	fetchLatestElderPlanData,
	getResidentIds,
} from '../../services/ElderService';
import { toast } from 'react-toastify';
import { getPlanValidation } from '../../services/CarePlanServices';
import { helperText } from '../../assets/theme/Theme';
import { border, dark, surface, teal } from '../../assets/theme/Colors';

// eslint-disable-next-line no-undef
const central_db_host = process.env.REACT_APP_UMS_API_URL;

const severityToStatus = {
	critical: 'error',
	warning: 'warning',
	suggestion: 'suggestion',
};

const chipStyle = {
	display: 'flex',
	alignItems: 'center',
	gap: '7px',
	fontSize: '12px',
	color: '#6b7a80',
	background: '#f7f9fa',
	padding: '6px 14px',
	borderRadius: '100px',
	border: '1px solid #e6e8eb',
};
const chipText = {
	fontSize: '12px',
	color: '#6b7a80',
};

const textInputStyle = {
	'& .MuiOutlinedInput-root': {
		fontSize: '12px',
		borderRadius: '8px',
		background: '#fff',
	},
};

function InitialSection() {
	return (
		<Box width={'100%'}>
			<Paper
				elevation={0}
				sx={{
					textAlign: 'center',
					p: '64px 32px',
					borderRadius: '12px',
				}}>
				{/* ICON CIRCLE */}
				<Box
					sx={{
						width: 80,
						height: 80,
						borderRadius: '50%',
						background: '#e6f7f7',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						margin: '0 auto 20px',
						border: '2px solid #66c7c7',
					}}>
					<Shield sx={{ fontSize: 34, color: '#00a7a7' }} />
				</Box>

				{/* TITLE */}
				<Typography
					sx={{
						color: '#1f2a2e',
						marginBottom: '10px',
						fontWeight: 700,
						fontSize: '20px',
					}}>
					Select a resident to validate their care plan
				</Typography>

				{/* DESCRIPTION */}
				<Typography
					sx={{
						color: '#6b7a80',
						maxWidth: 520,
						margin: '0 auto 28px',
						fontSize: '13.5px',
						lineHeight: 1.6,
					}}>
					SenzCare.AI will check the full care plan against the
					resident profile — flagging inconsistencies, missing
					personalization, clinical unsuitability, and CQC gaps.
				</Typography>

				{/* CHIPS CONTAINER */}
				<Box
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						gap: '10px',
						justifyContent: 'center',
					}}>
					{/* Chip 1 */}
					<Box sx={chipStyle}>
						<SwapHorizIcon
							sx={{ fontSize: 16, color: '#00a7a7' }}
						/>
						<Typography sx={chipText}>
							Information consistency
						</Typography>
					</Box>

					{/* Chip 2 */}
					<Box sx={chipStyle}>
						<FavoriteIcon sx={{ fontSize: 16, color: '#00a7a7' }} />
						<Typography sx={chipText}>Personalization</Typography>
					</Box>

					{/* Chip 3 */}
					<Box sx={chipStyle}>
						<LocalHospitalIcon
							sx={{ fontSize: 16, color: '#00a7a7' }}
						/>
						<Typography sx={chipText}>
							Clinical suitability
						</Typography>
					</Box>

					{/* Chip 4 */}
					<Box sx={chipStyle}>
						<WorkspacePremiumIcon
							sx={{ fontSize: 16, color: '#00a7a7' }}
						/>
						<Typography sx={chipText}>CQC alignment</Typography>
					</Box>
				</Box>
			</Paper>
		</Box>
	);
}

function CarePlanSection({
	title,
	badge,
	severity = 'warning',
	onClick,
	bodyContent = [],
}) {
	const getItemIcon = item => {
		if (item.status === 'error')
			return (
				<ErrorOutlineIcon
					sx={{ fontSize: 14, color: '#c84040', mt: '2px' }}
				/>
			);
		if (item.status === 'warning')
			return (
				<WarningAmberIcon
					sx={{ fontSize: 14, color: '#e0a030', mt: '2px' }}
				/>
			);
		if (item.status === 'suggestion')
			return (
				<LightbulbIcon
					sx={{ fontSize: 14, color: '#2d7a4a', mt: '2px' }}
				/>
			);
		return <CheckIcon sx={{ fontSize: 14, color: teal, mt: '2px' }} />;
	};

	const getUpdatedIcon = item => {
		if (item.isUpdated)
			return <CheckIcon sx={{ fontSize: 14, color: teal, mt: '2px' }} />;
		else return <></>;
	};

	return (
		<Box
			sx={{
				border: '1px solid #b3ede8',
				borderRadius: 2,
				overflow: 'hidden',
				mb: 1,
				backgroundColor: '#fff',
			}}>
			{/* HEADER */}
			<Box
				onClick={onClick}
				sx={{
					backgroundColor: '#e6f9f7',
					borderBottom: '1px solid #b3ede8',
					px: 2,
					py: 1.5,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					cursor: 'pointer',
				}}>
				<Typography
					sx={{ fontSize: 13, fontWeight: 600, color: '#0a5050' }}>
					{title}
				</Typography>

				<Chip
					label={badge}
					size="small"
					sx={{
						fontSize: 11,
						height: 20,
						backgroundColor:
							severity === 'error'
								? '#fde8e8'
								: severity === 'warning'
									? '#fffbf0'
									: severity === 'suggestion'
										? '#2d7a4a'
										: '#e8f5ee',
						color:
							severity === 'error'
								? '#c84040'
								: severity === 'warning'
									? '#c08020'
									: '#2a7040',
					}}
				/>
			</Box>

			{/* BODY */}
			<Box sx={{ p: 2 }}>
				{bodyContent.map((sub, i) => (
					<Box key={i} sx={{ mb: 2 }}>
						{/* SUB HEADER */}
						<Typography
							sx={{
								fontSize: 11,
								fontWeight: 700,
								color: '#0a5050',
								mb: 1,
								textTransform: 'uppercase',
							}}>
							{sub.label}
						</Typography>

						{/* ITEMS */}
						{sub.content.map((item, j) => (
							<Box
								key={j}
								sx={{
									display: 'flex',
									alignItems: 'flex-start',
									gap: 1,
									fontSize: 13,
									lineHeight: 1.5,
									px: item.status ? 1.2 : 0,
									py: item.status ? 0.7 : 0,
									mb: 1,
									borderRadius: item.status ? '6px' : 0,

									// FULL ROW HIGHLIGHT LOGIC
									backgroundColor:
										item.status === 'error'
											? '#fde8e8'
											: item.status === 'warning'
												? '#fffbf0'
												: item.status === 'suggestion'
													? '#f0faf5'
													: 'transparent',

									borderLeft:
										item.status === 'error'
											? '2px solid #7a1f1f'
											: item.status === 'warning'
												? '2px solid #7a5a12'
												: item.status === 'suggestion'
													? '2px solid #40b070'
													: '1px solid transparent',

									color:
										item.status === 'error'
											? '#7a1f1f'
											: item.status === 'warning'
												? '#7a5a12'
												: item.status === 'suggestion'
													? '#6b8888'
													: '#6b8888',
								}}>
								{getItemIcon(item)}
								{getUpdatedIcon(item)}
								<Typography sx={{ fontSize: 13 }}>
									{item.text}
								</Typography>
							</Box>
						))}
					</Box>
				))}
			</Box>
		</Box>
	);
}

function IssueCard({
	itemIndex,
	planIssue,
	onAccept,
	onDismiss,
	onUndo,
	onlyPreview = false,
	handleIssueChange,
}) {
	const [open, setOpen] = useState(false);

	const config = {
		critical: {
			bg: '#fff5f5',
			border: '#e05050',
			badgeBg: '#fde8e8',
			badgeColor: '#c84040',
			icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} />,
			label: 'Critical',
		},
		warning: {
			bg: '#fffbf0',
			border: '#e0a030',
			badgeBg: '#fef3dc',
			badgeColor: '#9a5f00',
			icon: <WarningAmberIcon sx={{ fontSize: 14 }} />,
			label: 'Warning',
		},
		suggestion: {
			bg: '#f0faf5',
			border: '#40b070',
			badgeBg: '#e8f5ee',
			badgeColor: '#2a7040',
			icon: <LightbulbIcon sx={{ fontSize: 14 }} />,
			label: 'Suggestion',
		},
	};

	const c = config[
		planIssue.sectionModified ? 'suggestion' : planIssue.severity
	] || {
		bg: '#fff',
		border: '#ccc',
		badgeBg: '#eee',
		badgeColor: '#333',
		icon: null,
		label: 'Unknown',
	};

	return (
		<Box
			sx={{
				borderRadius: '12px',
				padding: '14px 16px',
				background: c.bg,
				borderLeft: `3px solid ${c.border}`,
				mb: 1,
				filter: planIssue.isDismissed ? 'blur(0.1px)' : 'none',
				opacity: planIssue.isDismissed ? 0.5 : 1,
				pointerEvents: 'auto',
				transition: 'all 0.2s ease',
			}}>
			{/* ── TOP ROW ── */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '8px',
					mb: '7px',
				}}>
				{/* BADGE */}
				{planIssue.sectionModified ? (
					<Box sx={{ display: 'flex', flexDirection: 'row' }}>
						<Check sx={{ fontSize: 14, color: c.badgeColor }} />
						<Typography
							sx={{
								fontSize: 11,
								fontWeight: 600,
								color: c.badgeColor,
								marginLeft: '3px',
							}}>
							Fixed
						</Typography>
					</Box>
				) : (
					<Box
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: '4px',
							px: '8px',
							py: '3px',
							borderRadius: '100px',
							fontSize: '11px',
							fontWeight: 600,
							background: c.badgeBg,
							color: c.badgeColor,
						}}>
						{c.icon}
						{c.label}
					</Box>
				)}

				{/* SECTION */}
				<Typography
					sx={{
						fontSize: '11px',
						color: '#6b8888',
						fontWeight: 500,
						ml: 'auto',
					}}>
					{planIssue.categoryTitle}
				</Typography>
			</Box>

			{/* ── TITLE ── */}
			<Typography
				className="val-issue-title"
				sx={{
					fontSize: '13px',
					fontWeight: 600,
					color: '#1a2424',
					mb: '5px',
				}}>
				{planIssue.title}
			</Typography>

			{/* ── DETAIL ── */}
			<Typography
				sx={{
					fontSize: '12px',
					color: '#6b8888',
					lineHeight: 1.55,
					mb: '8px',
				}}>
				{planIssue.reason}
			</Typography>

			{/* ── FIX BOX (EXACT MATCH: display:none style behavior) ── */}
			<Box
				sx={{
					display: open ? 'block' : 'none',
					background: '#e6f9f7',
					border: '1px solid #b3ede8',
					borderRadius: '8px',
					padding: '10px 12px',
					mt: '6px',
				}}>
				{/* LABEL */}
				<Typography
					sx={{
						fontSize: '10px',
						fontWeight: 700,
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
						color: '#1a5c5c',
						display: 'flex',
						alignItems: 'center',
						gap: '5px',
						mb: '6px',
					}}>
					<AutoAwesomeIcon sx={{ fontSize: 12 }} />
					AI Suggested Fix
				</Typography>

				{/* CONTENT EDITABLE */}
				<Box
					sx={{
						fontSize: '12.5px',
						color: '#1a2424',
						lineHeight: 1.55,
						outline: 'none',
						minHeight: '36px',
						padding: '4px 0',
					}}>
					{planIssue.oldInfo && (
						<Box sx={{ marginBottom: '10px' }}>
							<Typography
								sx={{
									color: '#1a5c5c',
									fontSize: 12,
									fontWeight: 600,
								}}>
								Old info
							</Typography>
							<Typography
								sx={{
									color: '#1a2424',
									fontSize: 12,
								}}>
								{planIssue.oldInfo}
							</Typography>
						</Box>
					)}

					{planIssue.newInfo && (
						<Box>
							<Typography
								sx={{
									color: '#1a5c5c',
									fontSize: 12,
									fontWeight: 600,
									marginBottom: '5px',
								}}>
								New info
							</Typography>
							{onlyPreview ? (
								<Typography
									sx={{
										color: '#1a2424',
										fontSize: 12,
									}}>
									{planIssue.newInfo}
								</Typography>
							) : (
								<TextField
									fullWidth
									multiline
									variant="outlined"
									value={planIssue.newInfo}
									onChange={e =>
										handleIssueChange(
											itemIndex,
											e.target.value,
										)
									}
									sx={textInputStyle}
								/>
							)}
						</Box>
					)}
				</Box>

				{/* BUTTONS */}
				{!onlyPreview && (
					<Stack direction="row" spacing={1} mt={1}>
						<Button
							size="small"
							sx={{
								textTransform: 'none',
								background: teal,
								color: dark,
								fontWeight: 500,
								borderRadius: '8px',
								fontSize: '12px',
								gap: '5px',
								'&:hover': { background: '#00b0a0' },
							}}
							onClick={() => {
								setOpen(false);
								onAccept(planIssue);
							}}>
							<Check
								sx={{
									fontSize: 14,
									color: '#000',
								}}
							/>
							Accept & Apply
						</Button>

						<Button
							size="small"
							variant="outlined"
							sx={{
								textTransform: 'none',
								borderColor: border,
								color: '#1a2424',
								borderRadius: '8px',
								backgroundColor: '#FFF',
								fontSize: '12px',
								gap: '5px',
							}}
							onClick={() => {
								setOpen(false);
								onDismiss(planIssue);
							}}>
							<Close
								sx={{
									fontSize: 14,
									color: '#000',
								}}
							/>
							Dismiss
						</Button>
					</Stack>
				)}
			</Box>

			{/* ── SHOW AI FIX BUTTON ── */}
			{planIssue.sectionModified && !planIssue.isDismissed ? (
				<>
					<Box
						sx={{
							mt: '6px',
							display: 'inline-flex',
							alignItems: 'center',
							gap: '5px',
							fontSize: '11px',
							fontWeight: 600,
							color: '#1a5c5c',
							background: 'none',
							border: '1px solid #b3ede8', // fixed here
							borderRadius: '100px',
							padding: '4px 10px',
							cursor: 'pointer',
							textTransform: 'none',
						}}>
						<Check sx={{ fontSize: 11 }} />
						{'Fix applied'}
					</Box>
					<Button
						onClick={() => onUndo(planIssue, true)}
						sx={{
							ml: '5px',
							display: 'inline-flex',
							alignItems: 'center',
							gap: '5px',
							fontSize: '11px',
							fontWeight: 600,
							color: '#1a5c5c',
							background: 'none',
							border: '1px solid #b3ede8', // fixed here
							borderRadius: '100px',
							padding: '3px 10px',
							cursor: 'pointer',
							textTransform: 'none',
						}}>
						<AutoAwesomeIcon sx={{ fontSize: 11 }} />
						Undo change
					</Button>
				</>
			) : planIssue.isDismissed && !planIssue.sectionModified ? (
				<Button
					onClick={() => onUndo(planIssue)}
					sx={{
						mt: '6px',
						display: 'inline-flex',
						alignItems: 'center',
						gap: '5px',
						fontSize: '11px',
						fontWeight: 600,
						color: '#1a5c5c',
						background: 'none',
						border: '1px solid #b3ede8',
						borderRadius: '100px',
						padding: '4px 10px',
						cursor: 'pointer',
						textTransform: 'none',
						'&:hover': {
							background: '#e6f9f7',
							borderColor: teal,
						},
					}}>
					<AutoAwesomeIcon sx={{ fontSize: 11 }} />
					Undo change
				</Button>
			) : (
				<Button
					onClick={() => setOpen(!open)}
					sx={{
						mt: '6px',
						display: 'inline-flex',
						alignItems: 'center',
						gap: '5px',
						fontSize: '11px',
						fontWeight: 600,
						color: '#1a5c5c',
						background: 'none',
						border: '1px solid #b3ede8', // fixed here
						borderRadius: '100px',
						padding: '4px 10px',
						cursor: 'pointer',
						textTransform: 'none',
						'&:hover': {
							background: '#e6f9f7',
							borderColor: teal, // optional but nice
						},
					}}>
					<AutoAwesomeIcon sx={{ fontSize: 11 }} />
					{open ? 'Hide AI fix' : 'Show AI fix'}
				</Button>
			)}
		</Box>
	);
}

function FullPlanSection({
	currentFullPlan,
	onClose,
	title,
	buttonOneText,
	buttonOneClick,
	compareSideBySide = false,
	rawPlan,
	editingSection,
	onPlanChange,
	handleTextChange,
}) {
	return (
		<Grid container marginTop={'10px'}>
			<Box
				sx={{
					width: '100%',
					display: 'flex',
					gap: '8px',
					flexShrink: 0,
					backgroundColor: '#fff',
					padding: '8px 16px',
					marginBottom: '15px',
					borderRadius: '8px',
					flexDirection: 'column',
				}}>
				<Stack
					direction="row"
					width="100%"
					justifyContent="space-between"
					alignItems="center"
					mb={2}>
					<Typography
						variant="subtitle2"
						sx={{
							letterSpacing: 1,
							color: '#6b8888',
							fontWeight: 700,
							fontSize: 12,
						}}>
						{title}
					</Typography>
					<Box>
						<Button
							onClick={buttonOneClick}
							startIcon={
								<AutoAwesomeOutlined
									sx={{ width: 16, height: 16 }}
								/>
							}
							sx={{
								backgroundColor: teal,
								color: dark,
								fontSize: '12px',
								fontWeight: 600,
								padding: '8px 16px',
								borderRadius: '8px',
								textTransform: 'none',
								boxShadow: 'none',
								marginRight: '8px',
								'&:hover': {
									backgroundColor: '#00b0a0',
									boxShadow: 'none',
								},
							}}>
							{buttonOneText}
						</Button>

						{/* OUTLINE BUTTON */}
						<Button
							onClick={onClose}
							startIcon={<Close sx={{ width: 16, height: 16 }} />}
							sx={{
								backgroundColor: '#ffffff',
								color: '#1a2424',
								fontSize: '12px',
								fontWeight: 500,
								padding: '8px 16px',
								borderRadius: '8px',
								textTransform: 'none',
								border: '1px solid #d8e8e8',
								boxShadow: 'none',

								'&:hover': {
									backgroundColor: surface,
									borderColor: teal,
								},
							}}>
							Close
						</Button>
					</Box>
				</Stack>
				{compareSideBySide ? (
					<Grid container columnSpacing={1}>
						<Grid item xs={6}>
							<FinalPlanPreview plan={rawPlan} />
						</Grid>
						<Grid item xs={6}>
							<FinalPlanPreview
								isEnableEdit={true}
								plan={currentFullPlan}
								editingSection={editingSection}
								onPlanChange={onPlanChange}
								handleTextChange={handleTextChange}
							/>
						</Grid>
					</Grid>
				) : (
					<FinalPlanPreview
						isEnableEdit={true}
						plan={currentFullPlan}
						editingSection={editingSection}
						onPlanChange={onPlanChange}
						handleTextChange={handleTextChange}
					/>
				)}
				<Stack
					direction="row"
					width="100%"
					justifyContent="flex-end"
					alignItems="center"
					mt={1}>
					<Box>
						<Button
							onClick={buttonOneClick}
							startIcon={
								<AutoAwesomeOutlined
									sx={{ width: 16, height: 16 }}
								/>
							}
							sx={{
								backgroundColor: teal,
								color: dark,
								fontSize: '12px',
								fontWeight: 600,
								padding: '8px 16px',
								borderRadius: '8px',
								textTransform: 'none',
								boxShadow: 'none',
								marginRight: '8px',
								'&:hover': {
									backgroundColor: '#00b0a0',
									boxShadow: 'none',
								},
							}}>
							{buttonOneText}
						</Button>

						{/* OUTLINE BUTTON */}
						<Button
							onClick={onClose}
							startIcon={<Close sx={{ width: 16, height: 16 }} />}
							sx={{
								backgroundColor: '#ffffff',
								color: '#1a2424',
								fontSize: '12px',
								fontWeight: 500,
								padding: '8px 16px',
								borderRadius: '8px',
								textTransform: 'none',
								border: '1px solid #d8e8e8',
								boxShadow: 'none',

								'&:hover': {
									backgroundColor: surface,
									borderColor: teal,
								},
							}}>
							Close
						</Button>
					</Box>
				</Stack>
			</Box>
		</Grid>
	);
}

function FinalPlanPreview({
	plan,
	editingSection,
	onPlanChange,
	handleTextChange,
	isEnableEdit = false,
}) {
	function onShowChangeType(type) {
		return type === 'new'
			? 'New:'
			: type === 'updated'
				? 'Updated:'
				: 'Removed';
	}

	return (
		<Stack spacing={1}>
			{plan.map((section, sectionIndex) => (
				<Box
					key={sectionIndex}
					sx={{
						border: '1px solid #d8e8e8',
						borderRadius: '12px',
						overflow: 'hidden',
						mb: 1.5,
					}}>
					{/* SECTION HEADER */}
					<Box
						sx={{
							background: '#e6f9f7',
							borderBottom: '1px solid #b3ede8',
							p: '12px 16px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
							}}>
							<Typography
								sx={{
									fontSize: 13,
									fontWeight: 700,
									color: '#0a5050',
									mr: 1,
								}}>
								{section.title}
							</Typography>

							{section.isModified && (
								<Chip
									icon={<AutoAwesome sx={{ fontSize: 10 }} />}
									label={`Section modified`}
									size="small"
									sx={{
										height: 24,
										bgcolor: dark,
										color: teal,
										fontSize: 11,
										fontWeight: 600,
									}}
								/>
							)}
						</Box>
						{isEnableEdit && (
							<Button
								size="small"
								variant="outlined"
								startIcon={
									<Edit
										fontSize="small"
										sx={{ width: 12, height: 12 }}
									/>
								}
								onClick={() =>
									onPlanChange(
										editingSection === sectionIndex
											? null
											: sectionIndex,
									)
								}
								sx={{
									minWidth: 0,
									height: 26,
									fontSize: 11,
									textTransform: 'none',
									borderRadius: '6px',
									borderColor: border,
									color: '#1a2424',
									background: '#fff',
								}}>
								{editingSection === sectionIndex
									? 'Done'
									: 'Edit'}
							</Button>
						)}
					</Box>

					{/* BODY */}
					<Box
						sx={{
							p: '16px 16px 0px 16px',
						}}>
						{section.bodyContent.map((item, itemIndex) => (
							<Box key={itemIndex} sx={{ mb: 2 }}>
								{/* SUB TITLE */}
								{item.label && (
									<Typography
										sx={{
											fontSize: 12,
											fontWeight: 700,
											color: '#0a5050',
											mb: 1,
											textTransform: 'uppercase',
										}}>
										{item.label}
									</Typography>
								)}

								{/* CONTENT */}
								{(item.content || []).map(
									(content, contentIndex) => (
										<Box
											key={contentIndex}
											sx={{
												display: 'flex',
												alignItems: 'flex-start',
												gap: 1,
												mb: 1,
												background: content?.ai
													? '#e6f9f7'
													: 'transparent',
												borderRadius: content?.ai
													? '6px'
													: 0,
												p: content?.ai ? '6px 10px' : 0,
											}}>
											{content?.ai ? (
												<AutoAwesome
													sx={{
														fontSize: 15,
														color: '#0a5050',
														mt: '7px',
													}}
												/>
											) : (
												<Check
													sx={{
														fontSize: 15,
														color: teal,
														mt: '8px',
													}}
												/>
											)}
											{content?.ai && (
												<Typography
													sx={{
														fontSize: 13,
														fontWeight: 700,
														color: '#0a3a30',

														whiteSpace: 'nowrap',
														mt: '5px',
													}}>
													{onShowChangeType(
														content?.type,
													)}
												</Typography>
											)}
											{editingSection === sectionIndex ? (
												<TextField
													fullWidth
													multiline
													variant="outlined"
													value={content?.text}
													onChange={e =>
														handleTextChange(
															sectionIndex,
															itemIndex,
															contentIndex,
															e.target.value,
														)
													}
													sx={textInputStyle}
												/>
											) : (
												<Typography
													sx={{
														fontSize: 13,
														color: '#6b8888',
														lineHeight: 1.5,
														pt: '6px',
													}}>
													{content?.text}
												</Typography>
											)}
										</Box>
									),
								)}
							</Box>
						))}
					</Box>
				</Box>
			))}
		</Stack>
	);
}

function FinalConfirmationPopup({
	openPlanPopup,
	setOpenPlanPopup,
	planIssues,
	onStorePlanWithoutFullApproval,
}) {
	return (
		<Dialog
			open={openPlanPopup}
			onClose={() => setOpenPlanPopup(false)}
			maxWidth="md"
			fullWidth>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					fontSize: 14,
				}}>
				Attention
				<IconButton onClick={() => setOpenPlanPopup(false)}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				<Stack spacing={1}>
					{planIssues.map((planIssue, index) => (
						<IssueCard
							key={index}
							onlyPreview={true}
							planIssue={planIssue}
						/>
					))}
				</Stack>
			</DialogContent>
			<DialogActions
				sx={{
					p: 2,
					borderTop: '1px solid #e0e0e0',
					backgroundColor: '#fff',
					position: 'sticky',
					bottom: 0,
					zIndex: 1,
				}}>
				<Button
					onClick={onStorePlanWithoutFullApproval}
					startIcon={
						<AutoAwesomeOutlined sx={{ width: 16, height: 16 }} />
					}
					sx={{
						backgroundColor: teal,
						color: dark,
						fontSize: '12px',
						fontWeight: 600,
						padding: '8px 16px',
						borderRadius: '8px',
						textTransform: 'none',
						'&:hover': {
							backgroundColor: '#00b0a0',
						},
					}}>
					Continue
				</Button>

				<Button
					onClick={() => setOpenPlanPopup(false)}
					startIcon={
						<AutoAwesomeOutlined sx={{ width: 16, height: 16 }} />
					}
					sx={{
						backgroundColor: '#ffffff',
						color: '#1a2424',
						fontSize: '12px',
						fontWeight: 500,
						padding: '8px 16px',
						borderRadius: '8px',
						textTransform: 'none',
						border: '1px solid #d8e8e8',
						boxShadow: 'none',

						'&:hover': {
							backgroundColor: surface,
							borderColor: teal,
						},
					}}>
					Review Again
				</Button>
			</DialogActions>
		</Dialog>
	);
}

function CarePlanValidator() {
	const sectionRef = useRef(null);
	const token = getAccessToken();
	const homeId = getElderHomeId();
	const [id, setId] = useState(null);
	const [idError, setIdError] = useState({ status: false, msg: '' });
	const [currentPlan, setCurrentPlan] = useState([]);
	const [currentPlanData, setCurrentPlanData] = useState([]);
	const [currentRawPlan, setCurrentRawPlan] = useState([]);
	const [currentFullPlan, setCurrentFullPlan] = useState([]);
	const [currentFullPlanData, setCurrentFullPlanData] = useState([]);
	const [finalFullPlan, setFinalFullPlan] = useState([]);
	const [showFullPlan, setShowFullPlan] = useState(false);
	const [showFinalPlanPreview, setShowFinalPlanPreview] = useState(false);
	const [planIssues, setPlanIssues] = useState([]);
	const [planIssuesData, setPlanIssuesData] = useState([]);
	const [planIssuesNotResolved, setPlanIssuesNotResolved] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [loadingMsg, setLoadingMsg] = useState('');
	const [openPlanPopup, setOpenPlanPopup] = useState(false);
	const [residentIds, setResidentIds] = useState([]);
	const [residentFetchedIds, setResidentFetchedIds] = useState([]);
	const [isPlanGenerated, setIsPlanGenerated] = useState(false);
	const [selectedResidentName, setSelectedResidentName] = useState('');
	const [editingSection, setEditingSection] = useState(null);
	const [planErrorMsg, setPlanErrorMsg] = useState(
		'No plan found for this resident',
	);
	const onShowSaveButton = planIssuesNotResolved.length !== planIssues.length;

	useEffect(() => {
		const residentId = localStorage.getItem('resident_id');
		setId(residentId);
		fetchResidentIds();
	}, []);

	async function fetchResidentIds() {
		const response = await getResidentIds(homeId, token, 'optimized');

		if (response.message === 'Success') {
			const newIds = response.content.map(item =>
				item.isFromExternalApi
					? item.name
					: `${item.elderId} (${item.name})`,
			);

			setResidentFetchedIds(response.content);
			setResidentIds(newIds);
		} else {
			toast.error(response.message);
		}
	}

	function onChangeSelection(value) {
		setId(value);
		setIdError({ status: false, msg: '' });
	}

	function onValidatePlan() {
		if (!id || id === '') {
			setIdError({ status: true, msg: 'Please select a resident' });
			return;
		}

		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		setSelectedResidentName(selectedElder.name);

		if (selectedElder?.isFromExternalApi) {
			onFetchCurrentPlanFromDB(true, selectedElder);
		} else {
			onFetchCurrentPlanFromDB(false, selectedElder);
		}
	}

	async function onFetchCurrentPlanFromDB(
		isExternalElder = false,
		elder = null,
	) {
		const elderId = isExternalElder ? elder.elderId : elder.id;

		let planURL = '';

		setLoadingMsg('Fetching Current Plan');
		setIsLoading(true);
		setIsPlanGenerated(false);

		let response;
		let optimizedPlanIsEx = false;
		if (isExternalElder) {
			const planDetails = await fetchExternalLatestElderPlanData(
				homeId,
				elderId,
				token,
			);
			optimizedPlanIsEx = planDetails.isPlanExternal;
			if (planDetails.isPlanExternal) {
				response = await fetchExternalElderPlan(planDetails.id, token);
				planURL = `${central_db_host}/external/plan/${planDetails.id}`;
			} else {
				response = await fetchCarePlanById(
					planDetails.id,
					token,
					'optimized',
				);
				planURL = `${central_db_host}/plan/optimized/${planDetails.id}`;
			}
		} else {
			const manualPlanDetails = await fetchLatestElderPlanData(
				elderId,
				token,
			);
			const planId = manualPlanDetails.id;
			const planType =
				manualPlanDetails.planType === 'optimized'
					? 'optimized'
					: 'initial';
			response = await fetchCarePlanById(planId, token, planType);
			planURL = `${central_db_host}/plan/${planType}/${planId}`;
		}

		if (!response.error) {
			const fetchedPlan = optimizedPlanIsEx
				? response.carePlan.plan
				: response.plan;
			const planData = cleanCarePlan(fetchedPlan);
			if (!planData || !planData.length === 0) {
				setIsLoading(false);
				setLoadingMsg('');
				setPlanErrorMsg('No plan found for this resident');
				toast.error('No plan available to validate');
				return;
			}
			setCurrentPlan(planData);
			setCurrentRawPlan(planData);
			onFetchPlanValidation(
				elderId,
				isExternalElder,
				planURL,
				elder.id,
				planData,
			);
		} else {
			toast.error(response.message);
			setIsLoading(false);
			setLoadingMsg('');
		}
	}

	async function onFetchPlanValidation(
		elderId,
		isExternalElder = false,
		planURL,
		elderDocId,
		plan,
	) {
		setLoadingMsg('Validating Current Plan');

		let body = {};

		if (isExternalElder) {
			body = {
				elder_id: elderId,
				care_plan_endpoint_url: planURL,
				care_plan_token: token,
				reviews_endpoint_url: `${central_db_host}/care/elder/care_reviews?elder_id=${elderId}`,
				reviews_token: token,
				elder_profile_endpoint_url: `${central_db_host}/external/elder/${elderDocId}`,
				elder_profile_token: token,
			};
		} else {
			body = {
				elder_id: elderId,
				care_plan_endpoint_url: planURL,
				care_plan_token: token,
				reviews_endpoint_url: `${central_db_host}/note/elder/${elderId}`,
				reviews_token: token,
				elder_profile_endpoint_url: `${central_db_host}/elder/${elderId}`,
				elder_profile_token: token,
			};
		}

		const response = await getPlanValidation(body);
		if (!response.error) {
			if (!response.categories || response.categories.length === 0) {
				setPlanIssues([]);
				setIsLoading(false);
				setIsPlanGenerated(true);
				setLoadingMsg('');
				setCurrentPlan([]);
				setPlanErrorMsg('Hiding plan due to no ai suggestions');
				return;
			}
			const filletedPlan = filterPlanByStrictChanges(plan, response);
			const updatedPlan = applyValidationToPlan(filletedPlan, response);
			setCurrentPlan(updatedPlan);
			const structureFullPlan = applyCategoryChanges(
				plan,
				response.categories,
			);
			setCurrentFullPlan(structureFullPlan);
			const flattenedChanges = extractValidationItems(
				response.categories,
			);
			setPlanIssues(flattenedChanges);
			setPlanIssuesNotResolved(flattenedChanges);

			// Duplicate data for reset validations
			setPlanIssuesData(flattenedChanges);
			setCurrentFullPlanData(structureFullPlan);
			setCurrentPlanData(updatedPlan);
			setIsPlanGenerated(true);
			setPlanErrorMsg('');
		} else {
			toast.error(response.message);
		}

		setIsLoading(false);
		setLoadingMsg('');
	}

	/**
	 * Step 01 - Filter out sections that don't have any changes
	 * Used in `onFetchPlanValidation`
	 */
	function filterPlanByStrictChanges(plan, validationData) {
		return plan.filter(section => {
			// find matching category
			const category = validationData.categories.find(
				c =>
					c.category_title?.trim().toLowerCase() ===
					section.title?.trim().toLowerCase(),
			);

			// CASE 1: no matching category → REMOVE section
			if (!category) return false;

			// CASE 2: category exists but no changes → REMOVE section
			const hasChanges = (category.subsections || []).some(sub => {
				return (
					(sub.add?.length || 0) > 0 ||
					(sub.update?.length || 0) > 0 ||
					(sub.remove?.length || 0) > 0
				);
			});

			if (!hasChanges) return false;

			// keep only if real changes exist
			return true;
		});
	}

	/**
	 * Step 02 - Check for changes and apply them to the plan
	 * Used in `onFetchPlanValidation`
	 */
	function applyValidationToPlan(plan, validationData) {
		const updatedPlan = structuredClone(plan);

		updatedPlan.forEach(section => {
			const category = validationData.categories.find(
				c =>
					c.category_title?.trim().toLowerCase() ===
					section.title?.trim().toLowerCase(),
			);

			if (!category) return;

			category.subsections.forEach(subsection => {
				const targetSubsection = section.bodyContent.find(
					bc =>
						bc.label?.trim().toLowerCase() ===
						subsection.subsection_title?.trim().toLowerCase(),
				);

				if (!targetSubsection?.content) return;

				// REMOVE (highest -> lowest)
				[...(subsection.remove || [])]
					.sort(
						(a, b) =>
							(b.position?.position_index ?? 0) -
							(a.position?.position_index ?? 0),
					)
					.forEach(change => {
						const index = change.position?.position_index;

						const targetItem = targetSubsection.content[index];

						if (!targetItem) return;

						targetItem.status =
							severityToStatus?.[change.severity] || 'suggestion';

						targetItem.issue = {
							severity: change.severity,
							title: change.title ? change.title : 'No title',
							reason: change.reason
								? change.reason
								: 'Reason not available',
						};

						targetItem.isUpdated = false;
						targetItem.isRemoved = true;
					});

				// UPDATE
				(subsection.update || []).forEach(change => {
					const index = change.position?.position_index;

					const targetItem = targetSubsection.content[index];

					if (!targetItem) return;

					targetItem.status =
						severityToStatus?.[change.severity] || 'warning';

					targetItem.issue = {
						severity: change.severity,
						title: change.title,
						reason: change.reason,
					};

					targetItem.isUpdated = false;
					targetItem.isModified = true;
				});

				// ADD (lowest -> highest)
				[...(subsection.add || [])]
					.sort(
						(a, b) =>
							(a.position?.position_index ?? 0) -
							(b.position?.position_index ?? 0),
					)
					.forEach(change => {
						const index = change.position?.position_index ?? 0;

						targetSubsection.content.splice(index, 0, {
							text: change.new_info,
							ai: true,
							status:
								severityToStatus?.[change.severity] ||
								'warning',
							issue: {
								severity: change.severity,
								title: change.title ? change.title : 'No title',
								reason: change.reason
									? change.reason
									: 'Reason not available',
							},
							isUpdated: false,
							isNew: true,
						});
					});
			});
		});

		return updatedPlan;
	}

	/**
	 * Step 03 - Extract changes from categories
	 * Used in `onFetchPlanValidation`
	 */
	function extractValidationItems(categories) {
		const results = [];

		categories.forEach(category => {
			category.subsections?.forEach(subsection => {
				['add', 'remove', 'update'].forEach(changeType => {
					subsection[changeType]?.forEach(change => {
						results.push({
							categoryTitle: category.category_title,
							categoryStatus: category.status,
							subsectionTitle: subsection.subsection_title,
							changeType,

							newInfo: change.new_info || null,
							oldInfo: change.old_info || null,

							positionIndex:
								change.position?.position_index ?? null,

							severity: change.severity || null,
							title: change.title || null,
							reason: change.reason || null,
							sectionModified: false,
						});
					});
				});
			});
		});

		return results;
	}

	function cleanCarePlan(plan) {
		if (!Array.isArray(plan)) return [];

		return plan
			.filter(section => Array.isArray(section?.bodyContent))
			.filter(section => section?.bodyContent?.length > 0)
			.map(section => {
				return {
					title: section?.title || '',
					badge: section?.badge || '',
					severity: section?.severity || 'check',

					bodyContent: section.bodyContent.map(sub => ({
						label: sub.label,
						content: sub.content.flatMap(item => {
							if (Array.isArray(item)) {
								return item.map(t => ({
									text: t,
									status: 'checked',
								}));
							}

							// if single string
							if (typeof item === 'string') {
								return [
									{
										text: item,
										status: 'checked',
									},
								];
							}

							return [];
						}),
					})),
				};
			});
	}

	const applyCategoryChanges = (plan, categories) => {
		const updatedPlan = structuredClone(plan);

		// Normalize existing content
		updatedPlan.forEach(section => {
			(section.bodyContent || []).forEach(subsection => {
				subsection.content = (subsection.content || []).map(item => ({
					...(typeof item === 'string' ? { text: item } : item),
					type: item.type || 'check',
					ai: item.ai || false,
				}));
			});
		});

		categories.forEach(category => {
			const section = updatedPlan.find(
				s => s.title === category.category_title,
			);

			if (!section) return;

			(category.subsections || []).forEach(subsection => {
				const targetSubsection = section.bodyContent.find(
					item => item.label === subsection.subsection_title,
				);

				if (!targetSubsection) return;

				const content = targetSubsection.content || [];

				// ADD
				(subsection.add || []).forEach(change => {
					const index =
						change.position?.position_index ?? content.length;

					content.splice(index, 0, {
						text: change.new_info,
						ai: true,
						type: 'new',
						changeId: change.change_id,
						severity: change.severity,
					});
				});

				// UPDATE
				(subsection.update || []).forEach(change => {
					const index = change.position?.position_index;

					if (index >= 0 && index < content.length) {
						content[index] = {
							...content[index],
							text: change.new_info,
							ai: true,
							type: 'updated',
							changeId: change.change_id,
							severity: change.severity,
						};
					}
				});

				// REMOVE
				(subsection.remove || []).forEach(change => {
					const index = change.position?.position_index;

					if (index >= 0 && index < content.length) {
						content[index] = {
							...content[index],
							ai: true,
							type: 'removed',
							changeId: change.change_id,
							severity: change.severity,
						};
					}
				});

				targetSubsection.content = content;
			});
		});

		updatedPlan.forEach(section => {
			section.isModified = section.bodyContent.some(item =>
				(item.content || []).some(content => content?.ai),
			);
		});

		return updatedPlan;
	};

	function onSaveFinalPlan() {
		if (planIssuesNotResolved.length > 0) {
			setOpenPlanPopup(true);
			return;
		} else {
			storeUpdatedPlan();
		}
	}

	function handleAccept(item) {
		const updatedPlan = currentPlan.map(section => {
			if (section.title === item.categoryTitle) {
				return {
					...section,
					bodyContent: section.bodyContent.map(subsection => {
						if (subsection.label === item.subsectionTitle) {
							return {
								...subsection,
								content: subsection.content.map(
									(content, index) => {
										const isTarget =
											section.title ===
												item.categoryTitle &&
											index === item.positionIndex;

										if (isTarget) {
											if (
												['add', 'remove'].includes(
													item.changeType,
												)
											) {
												return {
													...content,
													isUpdated: true,
												};
											} else {
												return {
													...content,
													text: item.newInfo,
													isUpdated: true,
												};
											}
										}
										return content;
									},
								),
							};
						}
						return subsection;
					}),
				};
			}
			return section;
		});

		setCurrentPlan(updatedPlan);

		const updatedIssue = planIssues.map(change => {
			const isTarget =
				change.categoryTitle === item.categoryTitle &&
				change.subsectionTitle === item.subsectionTitle &&
				change.positionIndex === item.positionIndex;

			if (isTarget) {
				return {
					...change,
					sectionModified: true,
					isDismissed: false,
				};
			}

			return change;
		});
		setPlanIssues(updatedIssue);

		// Removing the resolved issues
		const updatedNotResolvedIssue = planIssuesNotResolved.filter(
			change =>
				!(
					change.categoryTitle === item.categoryTitle &&
					change.subsectionTitle === item.subsectionTitle &&
					change.positionIndex === item.positionIndex
				),
		);
		setPlanIssuesNotResolved(updatedNotResolvedIssue);
	}

	function handleAcceptAll() {
		const updatedPlan = currentPlan.map(section => {
			return {
				...section,
				bodyContent: section.bodyContent.map(subsection => {
					return {
						...subsection,
						content: subsection.content.map((content, index) => {
							const currentIssue = planIssues.find(
								item =>
									item.categoryTitle === section.title &&
									item.subsectionTitle === subsection.label &&
									item.positionIndex === index,
							);

							if (!currentIssue) {
								return content;
							}

							//if the issue is already dismissed ignore the change
							if (currentIssue.isDismissed) {
								return content;
							}

							const isTarget =
								section.title === currentIssue.categoryTitle &&
								index === currentIssue.positionIndex;

							if (isTarget) {
								if (
									['add', 'remove'].includes(
										currentIssue.changeType,
									)
								) {
									return {
										...content,
										isUpdated: true,
									};
								} else {
									return {
										...content,
										text: currentIssue.newInfo,
										isUpdated: true,
									};
								}
							}

							return content;
						}),
					};
				}),
			};
		});
		setCurrentPlan(updatedPlan);

		const updatedIssue = planIssues.map(issue => {
			if (issue.isDismissed) {
				return issue;
			}

			return {
				...issue,
				sectionModified: true,
			};
		});
		setPlanIssues(updatedIssue);
		// Removing all resolved issues
		setPlanIssuesNotResolved([]);
	}

	function handleUndo(item, undoChangeInPlan = false) {
		if (undoChangeInPlan) {
			const updatedPlan = currentPlan.map(section => ({
				...section,
				bodyContent: section.bodyContent.map(subsection => {
					if (subsection.label === item.subsectionTitle) {
						return {
							...subsection,
							content: subsection.content.map(
								(content, index) => {
									const isTarget =
										section.title === item.categoryTitle &&
										index === item.positionIndex;

									if (isTarget) {
										if (item.changeType === 'add') {
											return {
												...content,
												text: item.newInfo,
												isUpdated: false,
											};
										} else {
											return {
												...content,
												text: item.oldInfo,
												isUpdated: false,
											};
										}
									}

									return content;
								},
							),
						};
					}
					return subsection;
				}),
			}));

			setCurrentPlan(updatedPlan);
		}

		const updatedPlanIssues = planIssues.map(change => {
			const isTarget =
				change.categoryTitle === item.categoryTitle &&
				change.subsectionTitle === item.subsectionTitle &&
				change.positionIndex === item.positionIndex;

			if (isTarget) {
				return {
					...change,
					isUpdated: false,
					isDismissed: false,
					sectionModified: false,
				};
			}
			return change;
		});
		setPlanIssues(updatedPlanIssues);
		setPlanIssuesNotResolved(prevState => [...prevState, item]);
	}

	function handleDismiss(item) {
		const updatedIssue = planIssues.map(change => {
			const isTarget =
				change.categoryTitle === item.categoryTitle &&
				change.subsectionTitle === item.subsectionTitle &&
				change.positionIndex === item.positionIndex;

			if (isTarget) {
				return {
					...change,
					sectionModified: false,
					isDismissed: true,
				};
			}

			return change;
		});
		setPlanIssues(updatedIssue);

		const updatedNotResolvedIssue = planIssuesNotResolved.filter(
			change =>
				!(
					change.categoryTitle === item.categoryTitle &&
					change.subsectionTitle === item.subsectionTitle &&
					change.positionIndex === item.positionIndex
				),
		);
		setPlanIssuesNotResolved(updatedNotResolvedIssue);
	}

	/**
	 * Only storing after all issues are resolved in plan
	 */
	function storeUpdatedPlan() {
		// currentPlanData is the original plan fetched from DB (we updating this with new changes before saving)
		const updatedPlan = currentRawPlan.map(section => ({
			...section,
			bodyContent: section.bodyContent.map(subsection => {
				const currentIssues = planIssues.filter(
					change =>
						change.categoryTitle === section.title &&
						change.subsectionTitle === subsection.label &&
						!change.isDismissed,
				);

				if (currentIssues.length === 0) {
					return subsection;
				}

				const updatedContent = [...subsection.content];

				// Remove
				currentIssues
					.filter(change => change.changeType === 'remove')
					.sort((a, b) => b.positionIndex - a.positionIndex)
					.forEach(change => {
						const index = change.positionIndex;
						if (index >= 0 && index < updatedContent.length) {
							updatedContent.splice(index, 1);
						}
					});

				// Update
				currentIssues
					.filter(change => change.changeType === 'update')
					.forEach(change => {
						const index = change.positionIndex;

						if (index >= 0 && index < updatedContent.length) {
							updatedContent[index] = {
								...updatedContent[index],
								text: change.newInfo,
								type: 'updated',
								ai: true,
							};
						}
					});

				// Add
				currentIssues
					.filter(change => change.changeType === 'add')
					.sort((a, b) => a.positionIndex - b.positionIndex)
					.forEach(change => {
						const index = Math.min(
							change.positionIndex,
							updatedContent.length,
						);

						updatedContent.splice(index, 0, {
							text: change.newInfo,
							type: 'new',
							ai: true,
						});
					});

				return {
					...subsection,
					content: updatedContent,
				};
			}),
		}));

		// This state holds final updated structure of the plan to have a final preview and to store plan in DB
		setFinalFullPlan(updatedPlan);
		setIsPlanGenerated(false);
		setShowFinalPlanPreview(true);
		setOpenPlanPopup(false);
	}

	function storePartiallyUpdatedPlan() {
		const updatedIssues = planIssues.filter(
			item => !item.isDismissed && item.sectionModified,
		);

		const updatedPlan = currentRawPlan.map(section => ({
			...section,
			bodyContent: section.bodyContent.map(subsection => {
				const subsectionIssues = updatedIssues.filter(
					change =>
						change.categoryTitle === section.title &&
						change.subsectionTitle === subsection.label,
				);

				if (!subsectionIssues.length) return subsection;

				// STEP 1: SNAPSHOT
				let content = [...subsection.content];

				// STEP 2: REMOVE (DESC)
				const removes = subsectionIssues
					.filter(c => c.changeType === 'remove')
					.sort((a, b) => b.positionIndex - a.positionIndex);

				for (const change of removes) {
					if (
						change.positionIndex >= 0 &&
						change.positionIndex < content.length
					) {
						content.splice(change.positionIndex, 1);
					}
				}

				// STEP 3: UPDATE (ON CLEAN ARRAY)
				const updates = subsectionIssues.filter(
					c => c.changeType === 'update',
				);

				for (const change of updates) {
					if (
						change.positionIndex >= 0 &&
						change.positionIndex < content.length
					) {
						content[change.positionIndex] = {
							...content[change.positionIndex],
							text: change.newInfo,
							type: 'update',
							ai: true,
						};
					}
				}

				// STEP 4: ADD (ASC ORDER)
				const adds = subsectionIssues
					.filter(c => c.changeType === 'add')
					.sort((a, b) => a.positionIndex - b.positionIndex);

				for (const change of adds) {
					const index = Math.min(
						change.positionIndex,
						content.length,
					);

					content.splice(index, 0, {
						text: change.newInfo,
						type: 'add',
						ai: true,
					});
				}

				return {
					...subsection,
					content,
				};
			}),
		}));

		// This state holds final updated structure of the plan to have a final preview and to store plan in DB
		setFinalFullPlan(updatedPlan);
		setIsPlanGenerated(false);
		setShowFinalPlanPreview(true);
		setOpenPlanPopup(false);
	}

	async function storeOptimizedPlanInDB(newPlan) {
		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		const restructuredPlan = newPlan.map(section => {
			return {
				title: section.title,
				bodyContent: section.bodyContent.map(subsection => {
					return {
						label: subsection.label,
						content: subsection.content.map(item => {
							return item.text;
						}),
					};
				}),
			};
		});

		const planData = {
			elderHomeId: homeId,
			elderId: selectedElder.id,
			generatedUserId: getUserId(),
			generatedUserName: getUserName(),
			plan: restructuredPlan,
		};

		const formData = new FormData();
		formData.append(
			'plan',
			new Blob([JSON.stringify(planData)], {
				type: 'application/json',
			}),
		);
		const response = await addOptimizedCarePlan(formData, token);

		if (!response.error) {
			toast.success('Care plan saved successfully');
			onResetToInitialState();
		} else {
			toast.error(response.message);
		}
	}

	function resetValidator() {
		setPlanIssues(planIssuesData);
		setPlanIssuesNotResolved(planIssuesData);
		setCurrentPlan(currentPlanData);
		setCurrentFullPlan(currentFullPlanData);
	}

	function onResetToInitialState() {
		setIsPlanGenerated(false);
		setShowFullPlan(false);
		setShowFinalPlanPreview(false);
		setIsLoading(false);
		setId('');
		setPlanIssues([]);
		setPlanIssuesNotResolved([]);
		setCurrentPlan([]);
		setCurrentFullPlan([]);
		setFinalFullPlan([]);
		setPlanErrorMsg('No plan found for this resident.');
	}

	function handleFullPlanTextChange(
		sectionIndex,
		itemIndex,
		contentIndex,
		value,
	) {
		const updated = [...currentFullPlan];
		updated[sectionIndex].bodyContent[itemIndex].content[
			contentIndex
		].text = value;
		setCurrentFullPlan(updated);
	}

	function handleFinalFullPlanTextChange(
		sectionIndex,
		itemIndex,
		contentIndex,
		value,
	) {
		const updated = [...finalFullPlan];
		updated[sectionIndex].bodyContent[itemIndex].content[
			contentIndex
		].text = value;
		setFinalFullPlan(updated);
	}

	function handleIssueChange(index, text) {
		setPlanIssues(prev =>
			prev.map((issue, i) =>
				i === index ? { ...issue, newInfo: text } : issue,
			),
		);
	}

	function onShowFullPlan() {
		setShowFullPlan(true);

		// Wait for React to render the section
		setTimeout(() => {
			sectionRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		}, 0);
	}

	return (
		<MainGrid>
			<Box width={'100%'}>
				<Box mb={3}>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="flex-start"
						flexWrap="wrap"
						gap={2}>
						<Box>
							<Typography
								sx={{
									fontSize: '22px',
									fontWeight: 600,
									color: dark,
									mb: 0.5,
								}}>
								Care Plan Quality Validator
							</Typography>

							<Typography
								sx={{
									fontSize: 14,
									color: '#6b8888',
								}}>
								AI-powered care plan audit system
							</Typography>
						</Box>
						<Box flexDirection={'row'} display={'flex'}>
							<Box>
								<Select
									size="small"
									displayEmpty
									value={id || ''}
									onChange={e =>
										onChangeSelection(e.target.value)
									}
									sx={{
										'& .MuiSelect-select': {
											fontSize: '13px',
											color: id ? '#000' : '#9aa5b1',
										},
									}}
									error={idError.status}
									renderValue={selected => {
										if (!selected) {
											return 'Select Resident';
										}

										return selected;
									}}>
									<MenuItem
										value=""
										disabled
										sx={{ fontSize: '13px' }}>
										Select Resident
									</MenuItem>

									{residentIds.map((item, index) => (
										<MenuItem
											key={index}
											value={item}
											sx={{ fontSize: '13px' }}>
											{item}
										</MenuItem>
									))}
								</Select>
								{idError.status && (
									<Typography style={helperText}>
										{idError.msg}
									</Typography>
								)}
							</Box>
							{id && (
								<Button
									onClick={onValidatePlan}
									startIcon={<Shield sx={{ fontSize: 16 }} />}
									sx={{
										backgroundColor: teal,
										color: dark,
										fontSize: '13px',
										marginLeft: '5px',
										fontWeight: 600,
										borderRadius: '8px',
										padding: '8px 16px',
										textTransform: 'none',
										boxShadow: 'none',
										'&:hover': {
											backgroundColor: '#00b0a0',
										},
										height: '40px',
									}}>
									Validate plan
								</Button>
							)}
						</Box>
					</Stack>
				</Box>
				{isPlanGenerated && !showFinalPlanPreview ? (
					<>
						{planIssues.length > 0 && (
							<Box
								sx={{
									display: 'flex',
									gap: '8px',
									flexShrink: 0,
									backgroundColor: '#fff',
									padding: '8px 16px',
									marginBottom: '15px',
									justifyContent: 'flex-end',
									borderRadius: '8px',
								}}>
								{/* TEAL PRIMARY BUTTON */}
								<Button
									onClick={handleAcceptAll}
									startIcon={
										<AutoAwesomeOutlined
											sx={{ width: 16, height: 16 }}
										/>
									}
									sx={{
										backgroundColor: teal,
										color: dark,
										fontSize: '12px',
										fontWeight: 600,
										padding: '8px 16px',
										borderRadius: '8px',
										textTransform: 'none',
										boxShadow: 'none',

										'&:hover': {
											backgroundColor: '#00b0a0',
											boxShadow: 'none',
										},
									}}>
									Apply All Fixes
								</Button>

								{/* OUTLINE BUTTON */}
								<Button
									onClick={onShowFullPlan}
									startIcon={
										<DescriptionRoundedIcon
											sx={{ width: 16, height: 16 }}
										/>
									}
									sx={{
										backgroundColor: '#ffffff',
										color: '#1a2424',
										fontSize: '12px',
										fontWeight: 500,
										padding: '8px 16px',
										borderRadius: '8px',
										textTransform: 'none',
										border: '1px solid #d8e8e8',
										boxShadow: 'none',

										'&:hover': {
											backgroundColor: surface,
											borderColor: teal,
										},
									}}>
									Compare Full Plan
								</Button>

								{/* OUTLINE BUTTON */}
								<Button
									onClick={resetValidator}
									startIcon={
										<RefreshRoundedIcon
											sx={{ width: 16, height: 16 }}
										/>
									}
									sx={{
										backgroundColor: '#ffffff',
										color: '#1a2424',
										fontSize: '12px',
										fontWeight: 500,
										padding: '8px 16px',
										borderRadius: '8px',
										textTransform: 'none',
										border: '1px solid #d8e8e8',
										boxShadow: 'none',

										'&:hover': {
											backgroundColor: surface,
											borderColor: teal,
										},
									}}>
									Re-validate
								</Button>
							</Box>
						)}
						<Grid container spacing={2}>
							{/* Left */}
							<Grid item xs={12} md={6}>
								<Stack
									direction="row"
									spacing={1}
									alignItems="center"
									mb={2}>
									<Typography
										variant="subtitle2"
										sx={{
											textTransform: 'uppercase',
											letterSpacing: 1,
											color: '#6b8888',
											fontWeight: 700,
											fontSize: 12,
										}}>
										Current Care Plan —{' '}
										{selectedResidentName}
									</Typography>
								</Stack>

								{currentPlan.length > 0 ? (
									currentPlan.map((section, sectionIndex) => (
										<CarePlanSection
											key={sectionIndex}
											title={section.title}
											bodyContent={section.bodyContent}
											badge={'warning'}
										/>
									))
								) : isLoading ? (
									<Card
										sx={{
											border: `1px solid ${border} `,
											borderRadius: '12px',
											height: '100%',
										}}>
										<CardContent sx={{ p: 2.5 }}>
											<Skeleton
												variant="rectangular"
												height={120}
												sx={{
													margin: 0,
													borderRadius: '10px',
												}}
											/>
										</CardContent>
									</Card>
								) : (
									<Card
										sx={{
											borderRadius: '18px',
											border: '1px solid #d8e8e8',
											boxShadow: 'none',
											alignItems: 'center',
										}}>
										<CardContent
											sx={{
												p: 3,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												minHeight: 300,
												color: '#6b8888',
												fontSize: 13,
												fontWeight: 500,
											}}>
											{planErrorMsg ??
												'No care plan found.'}
										</CardContent>
									</Card>
								)}
							</Grid>

							{/* Right */}
							<Grid item xs={12} md={6}>
								<Box>
									<Stack
										direction="row"
										spacing={1}
										alignItems="center"
										mb={2}>
										<Typography
											variant="subtitle2"
											sx={{
												textTransform: 'uppercase',
												letterSpacing: 1,
												color: '#6b8888',
												fontWeight: 700,
												fontSize: 12,
											}}>
											Issues & AI Suggested Fixes
										</Typography>

										{planIssues.length > 0 && (
											<Chip
												icon={
													<AutoAwesome
														sx={{ fontSize: 14 }}
													/>
												}
												label={`AI Analysis`}
												size="small"
												sx={{
													height: 24,
													bgcolor: dark,
													color: teal,
													fontSize: 11,
													fontWeight: 600,
												}}
											/>
										)}
									</Stack>

									{planIssues.length > 0 ? (
										<>
											<Stack spacing={1}>
												{planIssues.map(
													(planIssue, index) => (
														<IssueCard
															key={index}
															itemIndex={index}
															planIssue={
																planIssue
															}
															onAccept={
																handleAccept
															}
															onDismiss={
																handleDismiss
															}
															onUndo={handleUndo}
															handleIssueChange={
																handleIssueChange
															}
														/>
													),
												)}
											</Stack>
											<Stack
												direction="row"
												spacing={2}
												mt={3}>
												<Button
													disabled={
														planIssuesNotResolved.length ===
														0
													}
													onClick={handleAcceptAll}
													startIcon={
														<AutoAwesomeIcon
															sx={{
																fontSize: 16,
															}}
														/>
													}
													sx={{
														backgroundColor: teal,
														color: dark,
														fontSize: '13px',
														fontWeight: 600,
														borderRadius: '8px',
														padding: '8px 16px',
														textTransform: 'none',
														boxShadow: 'none',
														'&:hover': {
															backgroundColor:
																'#00b0a0',
														},
													}}>
													Accept All Remaining Fixes
												</Button>

												{/* Save Button (conditionally hidden like your original style) */}
												{onShowSaveButton && (
													<Button
														onClick={
															onSaveFinalPlan
														}
														startIcon={
															<SaveIcon
																sx={{
																	fontSize: 16,
																}}
															/>
														}
														variant="outlined"
														sx={{
															fontSize: '13px',
															fontWeight: 500,
															borderRadius: '8px',
															textTransform:
																'none',
															borderColor: border,
															color: '#1a2424',
															backgroundColor:
																'#ffffff',
															'&:hover': {
																backgroundColor:
																	surface,
																borderColor:
																	teal,
															},
														}}>
														Save Updated Plan
													</Button>
												)}
											</Stack>
										</>
									) : (
										<Card
											sx={{
												borderRadius: '18px',
												border: '1px solid #d8e8e8',
												boxShadow: 'none',
												alignItems: 'center',
											}}>
											<CardContent
												sx={{
													p: 3,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													minHeight: 300,
													color: '#6b8888',
													fontSize: 13,
													fontWeight: 500,
												}}>
												AI Suggestion not found
											</CardContent>
										</Card>
									)}
								</Box>
							</Grid>
						</Grid>
					</>
				) : showFinalPlanPreview && !isPlanGenerated ? (
					<FullPlanSection
						currentFullPlan={finalFullPlan}
						title={`Final Plan Preview`}
						onClose={onResetToInitialState}
						buttonOneText={'Save Plan in Profile'}
						buttonOneClick={() =>
							storeOptimizedPlanInDB(finalFullPlan)
						}
						editingSection={editingSection}
						onPlanChange={value => setEditingSection(value)}
						handleTextChange={handleFinalFullPlanTextChange}
					/>
				) : isLoading ? (
					<Box width={'100%'}>
						<Paper
							elevation={0}
							sx={{
								textAlign: 'center',
								p: '64px 32px',
								borderRadius: '12px',
							}}>
							{/* ICON CIRCLE */}
							<Box
								sx={{
									width: 80,
									height: 80,
									borderRadius: '50%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									margin: '0 auto 20px',
								}}>
								<CircularProgress
									size={60}
									thickness={2}
									sx={{
										color: '#00a7a7',
									}}
								/>
							</Box>

							{/* TITLE */}
							<Typography
								sx={{
									color: '#1f2a2e',
									marginBottom: '10px',
									fontWeight: 700,
									fontSize: '20px',
								}}>
								{loadingMsg}
							</Typography>

							{/* DESCRIPTION */}
							<Typography
								sx={{
									color: '#6b7a80',
									maxWidth: 520,
									margin: '0 auto 28px',
									fontSize: '13.5px',
									lineHeight: 1.6,
								}}>
								Checking against resident profile and CQC
								standards
							</Typography>
						</Paper>
					</Box>
				) : (
					<InitialSection />
				)}
				{showFullPlan && !showFinalPlanPreview && (
					<Box ref={sectionRef}>
						<FullPlanSection
							currentFullPlan={currentFullPlan}
							title={`Full Plan comparison - ${selectedResidentName}`}
							onClose={() => setShowFullPlan(false)}
							buttonOneText={'Continue'}
							buttonOneClick={() => {
								setFinalFullPlan(currentFullPlan);
								setIsPlanGenerated(false);
								setShowFinalPlanPreview(true);
								setOpenPlanPopup(false);
							}}
							compareSideBySide={true}
							rawPlan={currentRawPlan}
							editingSection={editingSection}
							onPlanChange={value => setEditingSection(value)}
							handleTextChange={handleFullPlanTextChange}
						/>
					</Box>
				)}
			</Box>
			<FinalConfirmationPopup
				openPlanPopup={openPlanPopup}
				setOpenPlanPopup={setOpenPlanPopup}
				planIssues={planIssuesNotResolved}
				onStorePlanWithoutFullApproval={storePartiallyUpdatedPlan}
			/>
		</MainGrid>
	);
}

export default CarePlanValidator;
